import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Square } from 'lucide-react';

// Cached noise buffers — generated once per AudioContext.sampleRate.
const noiseCache = { snare: null, hihat: null, rate: 0 };
const ensureNoiseCache = (ctx) => {
  if (noiseCache.rate === ctx.sampleRate) return noiseCache;
  const snare = ctx.createBuffer(1, ctx.sampleRate * 0.2, ctx.sampleRate);
  const sd = snare.getChannelData(0);
  for (let i = 0; i < snare.length; i++) sd[i] = Math.random() * 2 - 1;
  const hh = ctx.createBuffer(1, ctx.sampleRate * 0.05, ctx.sampleRate);
  const hd = hh.getChannelData(0);
  for (let i = 0; i < hh.length; i++) hd[i] = Math.random() * 2 - 1;
  noiseCache.snare = snare;
  noiseCache.hihat = hh;
  noiseCache.rate = ctx.sampleRate;
  return noiseCache;
};

const playSound = (ctx, type, when, masterGain) => {
  const cache = ensureNoiseCache(ctx);
  const out = masterGain || ctx.destination;
  switch (type) {
    case 'kick': {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(out);
      osc.frequency.setValueAtTime(150, when);
      osc.frequency.exponentialRampToValueAtTime(0.01, when + 0.5);
      gain.gain.setValueAtTime(1, when);
      gain.gain.exponentialRampToValueAtTime(0.01, when + 0.5);
      osc.start(when); osc.stop(when + 0.5);
      break;
    }
    case 'snare': {
      const osc = ctx.createOscillator();
      const og = ctx.createGain();
      osc.type = 'triangle';
      osc.connect(og); og.connect(out);
      osc.frequency.setValueAtTime(100, when);
      og.gain.setValueAtTime(0.7, when);
      og.gain.exponentialRampToValueAtTime(0.01, when + 0.1);
      osc.start(when); osc.stop(when + 0.1);
      const n = ctx.createBufferSource();
      n.buffer = cache.snare;
      const f = ctx.createBiquadFilter();
      f.type = 'highpass'; f.frequency.value = 1000;
      const ng = ctx.createGain();
      n.connect(f); f.connect(ng); ng.connect(out);
      ng.gain.setValueAtTime(0.5, when);
      ng.gain.exponentialRampToValueAtTime(0.01, when + 0.2);
      n.start(when);
      break;
    }
    case 'hihat': {
      const n = ctx.createBufferSource();
      n.buffer = cache.hihat;
      const f = ctx.createBiquadFilter();
      f.type = 'highpass'; f.frequency.value = 7000;
      const g = ctx.createGain();
      n.connect(f); f.connect(g); g.connect(out);
      g.gain.setValueAtTime(0.3, when);
      g.gain.exponentialRampToValueAtTime(0.01, when + 0.05);
      n.start(when);
      break;
    }
    case 'ride': {
      // Two stacked sines for a metallic shimmer.
      const o1 = ctx.createOscillator();
      const g1 = ctx.createGain();
      o1.type = 'sine';
      o1.connect(g1); g1.connect(out);
      o1.frequency.setValueAtTime(400, when);
      g1.gain.setValueAtTime(0.2, when);
      g1.gain.exponentialRampToValueAtTime(0.01, when + 0.4);
      o1.start(when); o1.stop(when + 0.4);
      const o2 = ctx.createOscillator();
      const g2 = ctx.createGain();
      o2.type = 'sine';
      o2.connect(g2); g2.connect(out);
      o2.frequency.setValueAtTime(800, when);
      g2.gain.setValueAtTime(0.1, when);
      g2.gain.exponentialRampToValueAtTime(0.01, when + 0.3);
      o2.start(when); o2.stop(when + 0.3);
      break;
    }
    default: break;
  }
};

// Each pattern carries its time signature and an array of eighth-note
// steps. 4/4 = 8 eighths per bar, 3/4 = 6, 6/8 = 6 grouped 2+2+2,
// 12/8 = 12. At each step, list which drums fire.
const PATTERNS = {
  rock: {
    meter: '4/4',
    steps: [
      ['kick', 'hihat'], ['hihat'],
      ['snare', 'hihat'], ['hihat'],
      ['kick', 'hihat'], ['hihat'],
      ['snare', 'hihat'], ['hihat'],
    ],
  },
  pop: {
    meter: '4/4',
    steps: [
      ['kick', 'hihat'], ['hihat'],
      ['snare', 'hihat'], ['hihat'],
      ['hihat'], ['kick', 'hihat'],
      ['snare', 'hihat'], ['hihat'],
    ],
  },
  ballad: {
    meter: '4/4',
    steps: [
      ['kick', 'hihat'], [],
      ['snare', 'hihat'], [],
      ['kick', 'hihat'], [],
      ['snare', 'hihat'], [],
    ],
  },
  folk: {
    meter: '4/4',
    steps: [
      ['kick'], ['hihat'],
      ['snare'], ['hihat'],
      ['kick'], ['hihat'],
      ['snare'], ['hihat'],
    ],
  },
  funk: {
    meter: '4/4',
    steps: [
      ['kick', 'hihat'], ['hihat'],
      ['hihat'], ['snare', 'hihat'],
      ['kick', 'hihat'], ['kick', 'hihat'],
      ['snare', 'hihat'], ['hihat'],
    ],
  },
  blues: {
    meter: '4/4',
    // Approximation of a shuffle pulse - kick on the offbeats of the
    // second half evokes the swung feel without true triplets.
    steps: [
      ['kick', 'hihat'], ['hihat'],
      ['snare', 'hihat'], ['kick', 'hihat'],
      ['kick', 'hihat'], ['hihat'],
      ['snare', 'hihat'], ['hihat'],
    ],
  },
  country: {
    meter: '4/4',
    // Classic boom-chick: kick on the downbeats, hihat on the upbeats,
    // snare on 2 and 4.
    steps: [
      ['kick'], ['hihat'],
      ['snare'], ['hihat'],
      ['kick'], ['hihat'],
      ['snare'], ['hihat'],
    ],
  },
  disco: {
    meter: '4/4',
    steps: [
      ['kick', 'hihat'], ['hihat'],
      ['kick', 'snare', 'hihat'], ['hihat'],
      ['kick', 'hihat'], ['hihat'],
      ['kick', 'snare', 'hihat'], ['hihat'],
    ],
  },
  punk: {
    meter: '4/4',
    steps: [
      ['kick', 'hihat'], ['hihat'],
      ['kick', 'snare', 'hihat'], ['hihat'],
      ['kick', 'hihat'], ['hihat'],
      ['kick', 'snare', 'hihat'], ['hihat'],
    ],
  },
  metal: {
    meter: '4/4',
    steps: [
      ['kick', 'hihat'], ['kick', 'hihat'],
      ['snare', 'hihat'], ['kick', 'hihat'],
      ['kick', 'hihat'], ['kick', 'hihat'],
      ['snare', 'hihat'], ['kick', 'hihat'],
    ],
  },
  hiphop: {
    meter: '4/4',
    steps: [
      ['kick', 'hihat'], ['hihat'],
      ['snare', 'hihat'], ['hihat'],
      ['hihat'], ['kick', 'hihat'],
      ['snare', 'hihat'], ['hihat'],
    ],
  },
  reggae: {
    meter: '4/4',
    steps: [
      [], ['hihat'],
      [], ['hihat'],
      ['kick', 'snare'], ['hihat'],
      [], ['hihat'],
    ],
  },
  jazz: {
    meter: '4/4',
    steps: [
      ['kick', 'ride'], ['ride'],
      ['snare', 'ride'], ['ride'],
      ['kick', 'ride'], ['ride'],
      ['snare', 'ride'], ['ride'],
    ],
  },
  latin: {
    meter: '4/4',
    steps: [
      ['kick', 'hihat'], ['hihat'],
      ['hihat'], ['kick', 'snare', 'hihat'],
      ['kick', 'hihat'], ['hihat'],
      ['snare', 'hihat'], ['kick', 'hihat'],
    ],
  },
  // ============ Triple and compound meters ============
  waltz: {
    meter: '3/4',
    // Classic waltz: ONE-two-three pulse, kick on 1, snare on 2 and 3,
    // hihat on every eighth note for a strict feel.
    steps: [
      ['kick', 'hihat'], ['hihat'],
      ['snare', 'hihat'], ['hihat'],
      ['snare', 'hihat'], ['hihat'],
    ],
  },
  ballad34: {
    meter: '3/4',
    // Slow 3/4 ballad: hihat on every eighth, kick on 1 only,
    // snare on beat 2 with a brushed feel.
    steps: [
      ['kick', 'hihat'], ['hihat'],
      ['snare', 'hihat'], ['hihat'],
      [], ['hihat'],
    ],
  },
  ballad68: {
    meter: '6/8',
    // Compound duple folk ballad: kick on 1 + 4, snare on 4, hihat on
    // every eighth. "House of the Rising Sun" / "Norwegian Wood" feel.
    steps: [
      ['kick', 'hihat'], ['hihat'], ['hihat'],
      ['kick', 'snare', 'hihat'], ['hihat'], ['hihat'],
    ],
  },
  jig: {
    meter: '6/8',
    // Irish jig: kick on 1 + 4 with snare accents on 3 + 6.
    steps: [
      ['kick'], [], ['snare'],
      ['kick'], [], ['snare'],
    ],
  },
  slowBlues: {
    meter: '12/8',
    // Slow 12/8 blues shuffle: kick on 1, snare on 7, ride/hihat
    // triplets on every eighth.
    steps: [
      ['kick', 'hihat'], [], ['hihat'],
      ['hihat'], [], ['hihat'],
      ['snare', 'hihat'], [], ['hihat'],
      ['hihat'], [], ['hihat'],
    ],
  },
  ballad128: {
    meter: '12/8',
    // Doo-wop / "When a Man Loves a Woman" sleepy 12/8.
    steps: [
      ['kick', 'hihat'], ['hihat'], ['hihat'],
      ['hihat'], ['hihat'], ['hihat'],
      ['snare', 'hihat'], ['hihat'], ['hihat'],
      ['hihat'], ['hihat'], ['hihat'],
    ],
  },
};

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_SEC = 0.1;

const MiniDrums = ({ initialBpm = 100, initialPattern = 'rock', accent = 'amber' }) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [bpm, setBpm] = useState(initialBpm);
  const [patternKey, setPatternKey] = useState(
    PATTERNS[initialPattern] ? initialPattern : 'rock'
  );

  useEffect(() => {
    if (PATTERNS[initialPattern]) setPatternKey(initialPattern);
  }, [initialPattern]);
  const [volume, setVolume] = useState(0.7);
  const [currentStep, setCurrentStep] = useState(0);

  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const isPlayingRef = useRef(false);
  const bpmRef = useRef(initialBpm);
  const patternRef = useRef(patternKey);
  const volRef = useRef(volume);
  const stepRef = useRef(0);
  const nextStepTimeRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => { bpmRef.current = bpm; }, [bpm]);
  useEffect(() => { patternRef.current = patternKey; }, [patternKey]);
  useEffect(() => { setBpm(initialBpm); }, [initialBpm]);
  useEffect(() => {
    volRef.current = volume;
    if (masterRef.current) masterRef.current.gain.value = volume;
  }, [volume]);

  const scheduler = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx || !isPlayingRef.current) return;
    const pattern = PATTERNS[patternRef.current] || PATTERNS.rock;
    const steps = pattern.steps;
    const eighth = 60.0 / bpmRef.current / 2;
    while (nextStepTimeRef.current < ctx.currentTime + SCHEDULE_AHEAD_SEC) {
      const stepIdx = stepRef.current % steps.length;
      const hits = steps[stepIdx] || [];
      for (const drum of hits) {
        playSound(ctx, drum, nextStepTimeRef.current, masterRef.current);
      }
      setCurrentStep(stepIdx);
      stepRef.current = (stepRef.current + 1) % steps.length;
      nextStepTimeRef.current += eighth;
    }
  }, []);

  const start = useCallback(() => {
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      masterRef.current = ctxRef.current.createGain();
      masterRef.current.gain.value = volRef.current;
      masterRef.current.connect(ctxRef.current.destination);
    }
    if (ctxRef.current.state === 'suspended') ctxRef.current.resume();
    isPlayingRef.current = true;
    setIsPlaying(true);
    stepRef.current = 0;
    nextStepTimeRef.current = ctxRef.current.currentTime;
    timerRef.current = setInterval(scheduler, LOOKAHEAD_MS);
  }, [scheduler]);

  const stop = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    setCurrentStep(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const toggle = () => (isPlayingRef.current ? stop() : start());

  useEffect(() => {
    // Cleanup on unmount: stop and close the audio context
    return () => {
      isPlayingRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (ctxRef.current) {
        try { ctxRef.current.close(); } catch (_) {}
        ctxRef.current = null;
      }
    };
  }, []);

  const accentClasses = {
    amber: { dot: 'bg-amber', btn: 'from-amber to-orange-500' },
    blue: { dot: 'bg-cyan-400', btn: 'from-blue to-cyan-400' },
    purple: { dot: 'bg-purple-400', btn: 'from-purple-500 to-pink-500' },
    green: { dot: 'bg-green-400', btn: 'from-green-500 to-emerald-500' },
  }[accent] || { dot: 'bg-amber', btn: 'from-amber to-orange-500' };

  const activePattern = PATTERNS[patternKey] || PATTERNS.rock;
  const stepCount = activePattern.steps.length;
  const meter = activePattern.meter;
  // Beats per bar = numerator of the time signature. For 6/8 and 12/8
  // we still show one dot per eighth (6 or 12 dots) since the pattern
  // is already eighth-quantised.
  const beatsPerBar = parseInt(meter.split('/')[0], 10) || 4;

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-5 py-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {Array.from({ length: stepCount }).map((_, i) => {
            // For 4/4: accent every other step (downbeats). For 6/8: accent
            // 0 and 3 (the two dotted-quarter beats). For 3/4: accent 0, 2, 4.
            // For 12/8: accent 0, 3, 6, 9.
            const isDownbeat =
              (meter === '4/4' && i % 2 === 0) ||
              (meter === '3/4' && i % 2 === 0) ||
              (meter === '6/8' && (i === 0 || i === 3)) ||
              (meter === '12/8' && i % 3 === 0);
            return (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-all ${
                  isPlaying && currentStep === i
                    ? `${accentClasses.dot} scale-150`
                    : isDownbeat ? 'bg-white/25' : 'bg-white/10'
                }`}
              />
            );
          })}
        </div>
        <button
          onClick={toggle}
          className={`w-10 h-10 rounded-full bg-gradient-to-r ${accentClasses.btn} text-black flex items-center justify-center hover:scale-105 transition-transform`}
          aria-label={isPlaying ? 'Stop drums' : 'Start drums'}
        >
          {isPlaying ? <Square className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current ml-0.5" />}
        </button>
      </div>

      <div className="flex items-center gap-3">
        <label className="text-zinc-500 text-xs uppercase tracking-wider">
          {t('drums.style', { defaultValue: 'Style' })}
        </label>
        <select
          value={patternKey}
          onChange={(e) => setPatternKey(e.target.value)}
          className="flex-1 bg-[#0A0A0A] border border-white/10 text-zinc-200 text-sm rounded px-2 py-1 focus:outline-none focus:border-amber/50"
        >
          {Object.entries(PATTERNS).map(([key, p]) => (
            <option key={key} value={key}>
              {key[0].toUpperCase() + key.slice(1)} · {p.meter}
            </option>
          ))}
        </select>
        <span className="font-mono text-amber text-sm w-20 text-right whitespace-nowrap">{meter} · {bpm} BPM</span>
      </div>

      <div className="flex items-center gap-3 text-xs text-zinc-400">
        <span>BPM</span>
        <input
          type="range"
          min={40}
          max={240}
          step={1}
          value={bpm}
          onChange={(e) => setBpm(Number(e.target.value))}
          className="flex-1 accent-amber"
        />
        <span className="w-12 text-right">{t('drums.volume', { defaultValue: 'Vol' })}</span>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={Math.round(volume * 100)}
          onChange={(e) => setVolume(Number(e.target.value) / 100)}
          className="w-20 accent-amber"
        />
      </div>
    </div>
  );
};

export default MiniDrums;
